<section class="home-shell">

  <!-- ── Page title + period filter ───────────────────────── -->
  <header class="home-header">
    <div class="title-block">
      <h1>Dashboard</h1>
      <p>Overview of file processing and tuning statistics</p>
    </div>

    <div class="period-controls" *ngIf="hasAdminAccess() && isLoggedIn()">
      <div class="period-toggle">
        <button type="button"
                [class.active]="periodService.mode() === 'TODAY'"
                (click)="setPeriod('TODAY')">Today</button>
        <button type="button"
                [class.active]="periodService.mode() === 'WEEK'"
                (click)="setPeriod('WEEK')">Week</button>
        <button type="button"
                [class.active]="periodService.mode() === 'MONTH'"
                (click)="setPeriod('MONTH')">Month</button>
      </div>
      <div class="date-picker"
           [class.active]="periodService.mode() === 'CUSTOM'">
        <input type="date"
               [ngModel]="customStartStr"
               (ngModelChange)="onCustomStartChange($event)"
               (focus)="setPeriod('CUSTOM')">
        <span class="date-separator">&ndash;</span>
        <input type="date"
               [ngModel]="customEndStr"
               (ngModelChange)="onCustomEndChange($event)"
               (focus)="setPeriod('CUSTOM')">
      </div>
    </div>
  </header>

  <app-dashboard-winols-search *ngIf="isLoggedIn()"></app-dashboard-winols-search>

  <!-- ── Imports KPIs (admin/superadmin only) ─────────────── -->
  <ng-container *ngIf="hasAdminAccess() && isLoggedIn()">
    <app-overview-kpi-cards
      [overview]="currentOverview()"
      [previousOverview]="previousOverview()"
      [isLoading]="isOverviewLoading()">
    </app-overview-kpi-cards>
  </ng-container>

  <!-- ── Match and Tune + Upload Pure Original (all roles) ─── -->
  <div class="row-2up">
    <app-match-tune-card (fileSelected)="openMatchTuneDialog($event)"></app-match-tune-card>

    <div class="upload-po-host">
      <app-pure-originals-upload></app-pure-originals-upload>
    </div>
  </div>

  <!-- ── My files and the team overview ────────────────────── -->
  <div *ngIf="isLoggedIn()" [class.row-2up]="hasAdminAccess()" [class.row-1up]="!hasAdminAccess()">
    @defer (on idle) {
      <app-claimed-files-v3-card mode="mine"></app-claimed-files-v3-card>
    } @placeholder {
      <div class="card-skeleton" style="min-height: 280px"></div>
    }

    <ng-container *ngIf="hasAdminAccess()">
      @defer (on idle) {
        <app-claimed-files-v3-card></app-claimed-files-v3-card>
      } @placeholder {
        <div class="card-skeleton" style="min-height: 378px"></div>
      }
    </ng-container>
  </div>

  <!-- ── Recent Matches (admin/superadmin only) ────────────── -->
  <ng-container *ngIf="hasAdminAccess() && isLoggedIn()">
    <div class="row-1up">
      @defer (on idle) {
        <app-recent-matches></app-recent-matches>
      } @placeholder {
        <div class="card-skeleton" style="min-height: 260px"></div>
      }
    </div>
  </ng-container>

  <!-- ── WinOLS Transactions + File Status Donut (admin/superadmin) -->
  <ng-container *ngIf="hasAdminAccess() && isLoggedIn()">
    <div class="row-2up">
      @defer (on idle) {
        <app-winols-transactions-card></app-winols-transactions-card>
      } @placeholder {
        <div class="card-skeleton" style="min-height: 400px"></div>
      }

      @defer (on idle) {
        <app-file-status-donut [overview]="currentOverview()" [isLoading]="isOverviewLoading()"></app-file-status-donut>
      } @placeholder {
        <div class="card-skeleton" style="min-height: 400px"></div>
      }
    </div>
  </ng-container>

  <!-- ── Missing Tunings and ECU rankings ─────────────────── -->
  <ng-container *ngIf="(isAdmin() || isModerator()) && isLoggedIn()">
    <div class="row-1up">
      @defer (on idle) {
        <app-missing-tunings-card></app-missing-tunings-card>
      } @placeholder {
        <div class="card-skeleton" style="min-height: 378px"></div>
      }
    </div>

    <div class="row-2up">
      @defer (on idle) {
        <app-top-ecus-card type="missing"></app-top-ecus-card>
      } @placeholder {
        <div class="card-skeleton" style="min-height: 320px"></div>
      }

      @defer (on idle) {
        <app-top-ecus-card type="partial"></app-top-ecus-card>
      } @placeholder {
        <div class="card-skeleton" style="min-height: 320px"></div>
      }
    </div>
  </ng-container>

  <!-- ══════════ DETAILED ANALYTICS (admin/superadmin only) ══════════ -->
  <ng-container *ngIf="hasAdminAccess() && isLoggedIn()">

    <div class="section-divider">
      <span class="divider-label">DETAILED ANALYTICS</span>
      <span class="divider-rule"></span>
      <span class="divider-hint">Drill-down metrics across the platform</span>
    </div>

    @defer (on idle) {
      <app-activity-trends></app-activity-trends>
    } @placeholder {
      <div class="card-skeleton" style="min-height: 322px"></div>
    }

    <div class="row-2up">
      @defer (on idle) {
        <app-top-rankings></app-top-rankings>
      } @placeholder {
        <div class="card-skeleton" style="min-height: 305px"></div>
      }

      @defer (on idle) {
        <app-po-analytics></app-po-analytics>
      } @placeholder {
        <div class="card-skeleton" style="min-height: 305px"></div>
      }
    </div>

    <div class="row-2up">
      @defer (on idle) {
        <app-user-activity></app-user-activity>
      } @placeholder {
        <div class="card-skeleton" style="min-height: 388px"></div>
      }

      @defer (on idle) {
        <app-winols-analytics></app-winols-analytics>
      } @placeholder {
        <div class="card-skeleton" style="min-height: 388px"></div>
      }
    </div>

    @defer (on idle) {
      <app-po-export-trends></app-po-export-trends>
    } @placeholder {
      <div class="card-skeleton" style="min-height: 302px"></div>
    }

    <div class="row-2up">
      @defer (on idle) {
        <app-version-and-updates></app-version-and-updates>
      } @placeholder {
        <div class="card-skeleton" style="min-height: 328px"></div>
      }

      @defer (on idle) {
        <app-ecu-matching-breakdown></app-ecu-matching-breakdown>
      } @placeholder {
        <div class="card-skeleton" style="min-height: 328px"></div>
      }
    </div>

  </ng-container>

</section>
